import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { AccountCircleRounded, AddRounded } from "@mui/icons-material";

import TagForm from "./TagForm";
import { backend_api } from "../../../../static/server";
import { getAllCustomField } from "../../../../redux/Apps/Lawyerapp/CustomFieldSlice";
import dayjs from "dayjs";
import axios from "axios";
import { pageScrollStyle } from "../../../../styles/styles";
import { useDialog } from "../../../DialogContext";
import noOption from "../../../../assets/no-option-found.webp";
import CustomFieldForm from "../../Settings/CustomFields/CustomFieldForm";
import {
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DrawerTemplate from "../../Cases/DrawerTemplate";
import {
  getAllFilterContacts,
  getAllTags,
} from "../../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import ContactMiniForm from "./ContactMiniForm";
import MatterApiField from "../../Settings/FieldsUi/MatterApiField";
import ContactApiField from "../../Settings/FieldsUi/ContactApiField";

const DotStepIcon = ({ active, completed }) => {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: completed || active ? "primary.light" : "#c4c4c4",
      }}
    />
  );
};

const CompanyContactForm = ({
  isEditContactMode,
  editData,
  handleCloseAddContect,
  activeContactType = "companies",
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);
  const { allCustomField } = useSelector((state) => state.customField);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCustomFields, setSelectedCustomFields] = useState([]);
  //tag option input field state
  const [tagOptions, setTagOptions] = useState([]);
  const [tagInputValue, setTagInputValue] = useState("");
  const [tagPage, setTagPage] = useState(1);
  const [tagHasMore, setTagHasMore] = useState(true);
  const [tagSearching, setTagSearching] = useState(false);
  //employe input field state
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeInputValue, setEmployeeInputValue] = useState("");
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(true);
  const [employeeSearching, setEmployeeSearching] = useState(false);
  const contactListType = useMemo(
    () => (activeContactType === "people" ? "person" : "company"),
    [activeContactType]
  );

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
    watch,
    setError,
  } = useForm({
    defaultValues: {
      contact_type: "company",
      company_name: "",
      tags: [],
      reminder_mail: "",
      mobile: "",
      website_url: "",
      address_1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      custom_fields: [],
      custom_fields_data: [],
      ledes_code: "",
      other_contact: [],
    },
  });

  const custom_fields = watch("custom_fields");
  const [activeIndex, setActiveIndex] = useState(0);
  const steps = [
    { label: "Basic Information", description: "Company name and tags" },
    {
      label: "Contact Information",
      description: "Email, phone, address details",
    },
    { label: "Additional Fields", description: "Custom fields and LEDES code" },
    {
      label: "Associated Employees",
      description: "Link employees to this company",
    },
  ];

  useEffect(() => {
    dispatch(
      getAllCustomField({
        url: `${backend_api}custom-field/?field_for=contact`,
        token: token,
      })
    );
    loadTags(1, "");
    loadEmployees(1, "");
  }, [dispatch, token]);

  useEffect(() => {
    if (!isEditContactMode || !editData) return;

    Object.keys(editData)?.forEach((key) => {
      if (key === "d_o_b" && editData[key]) {
        setValue(key, dayjs(editData[key]));
        return;
      }

      if (Array.isArray(editData[key])) {
        if (key === "tags") {
          const savedTags = editData[key].map((tag) =>
            typeof tag === "object" ? tag : { id: tag }
          );
          setTagOptions((prev) => {
            const map = new Map();
            savedTags
              ?.filter((tag) => tag?.id)
              ?.forEach((tag) => map.set(tag.id, tag));
            prev?.forEach((tag) => {
              if (tag?.id) {
                map.set(tag.id, { ...map.get(tag.id), ...tag });
              }
            });
            return Array.from(map.values());
          });

          setValue(
            key,
            savedTags.map((t) => t.id)
          );
        } else if (key === "other_contact") {
          const savedEmployees = editData[key].map((emp) =>
            typeof emp === "object" ? emp : { id: emp }
          );
          setEmployeeOptions((prev) => {
            const missing = savedEmployees.filter(
              (e) => !prev.some((p) => p.id === e.id)
            );
            return [...missing, ...prev];
          });
          setSelectedEmployees(savedEmployees);
          setValue(
            key,
            savedEmployees.map((e) => e.id)
          );
        } else if (key === "custom_fields" && allCustomField) {
          const fieldIds = editData[key].map((f) =>
            typeof f === "object" ? f.id : f
          );
          const ordered = fieldIds
            .map((id) => allCustomField.find((f) => f.id === id))
            .filter(Boolean);
          setSelectedCustomFields(ordered);
          if (editData.custom_fields_data) {
            const orderedData = ordered.map((field) => {
              const ex = editData.custom_fields_data.find(
                (d) => d.id === field.id
              );
              return (
                ex || {
                  id: field.id,
                  type: field.field_data?.type,
                  value: "",
                }
              );
            });
            setValue("custom_fields_data", orderedData);
          }
          setValue(key, fieldIds);
        } else {
          setValue(key, editData[key]);
        }
        return;
      }
      setValue(key, editData[key]);
    });
  }, [isEditContactMode, editData, allCustomField]);

  useEffect(() => {
    if (isEditContactMode || !allCustomField || selectedCustomFields.length > 0)
      return;
    const defaultFields = allCustomField?.filter(
      (f) => f?.field_data?.default_apply
    );
    if (defaultFields.length > 0) {
      const ids = defaultFields.map((f) => f.id);
      const data = defaultFields.map((f) => ({
        id: f.id,
        type: f.field_data?.type,
        value: "",
      }));
      setSelectedCustomFields(defaultFields);
      setValue("custom_fields", ids, { shouldValidate: true });
      setValue("custom_fields_data", data);
    }
  }, [allCustomField, selectedCustomFields.length]);

  const handleCustomFieldSelectionChange = (event, selectedOptions) => {
    event.preventDefault();
    const ids = selectedOptions.map((o) => o.id);
    const data = selectedOptions.map((o) => ({
      id: o.id,
      type: o.field_data?.type,
      value: "",
    }));
    setValue("custom_fields", ids, { shouldValidate: true });
    setValue("custom_fields_data", data);
    setSelectedCustomFields(selectedOptions);
  };

  const validateCustomFields = (fields, data) => {
    const errors = {};
    if (fields?.length > 0) {
      fields.forEach((id, index) => {
        const fieldData = allCustomField?.find((f) => f.id === id);
        if (fieldData?.field_data?.required) {
          const val = data?.[index]?.value;
          if (!val || val === "") {
            errors[`custom_fields_data.${index}`] = {
              type: "required",
              message: `${fieldData.field_name} is required`,
            };
          }
        }
      });
    }
    return errors;
  };

  const scrollToFirstError = (errs) => {
    const firstErrorField = Object.keys(errs)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  };

  const handleError = (formErrors) => {
    scrollToFirstError(formErrors);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const fieldErrors = validateCustomFields(
      data.custom_fields,
      data.custom_fields_data
    );
    if (Object.keys(fieldErrors).length > 0) {
      Object.entries(fieldErrors).forEach(([field, error]) =>
        setError(field, error)
      );
      scrollToFirstError(fieldErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const jsonData = { ...data, contact_type: "company" };
      if (jsonData.d_o_b) {
        jsonData.d_o_b = new Date(jsonData.d_o_b).toISOString();
      }
      const url = isEditContactMode
        ? `${backend_api}contact/create/${editData.id}/`
        : `${backend_api}contact/create/`;
      const method = isEditContactMode ? "PUT" : "POST";
      await axios({
        method,
        url,
        data: jsonData,
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      dispatch(
        getAllFilterContacts({
          url: `${backend_api}contact/retrieve/?contact_type=${contactListType}`,
          token,
          teamMemberLawyerId,
        })
      );
      handleCloseAddContect();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    reset();
    setSelectedCustomFields([]);
  };

  const mergeTagOptions = useCallback((prev = [], next = [], reset = false) => {
    const map = new Map();
    if (!reset) {
      prev?.forEach((tag) => {
        if (tag?.id) {
          map.set(tag.id, tag);
        }
      });
    }
    next?.forEach((tag) => {
      if (tag?.id) {
        map.set(tag.id, { ...map.get(tag.id), ...tag });
      }
    });
    return Array.from(map.values());
  }, []);

  const loadTags = useCallback(
    async (page = 1, search = "", reset = false) => {
      if (tagSearching) return;

      setTagSearching(true);
      try {
        const response = await dispatch(
          getAllTags({
            url: `${backend_api}info-tags/`,
            token: token,
            page: page,
            // pageSize: 20,
            search: search,
          })
        ).unwrap();

        setTagOptions((prev) =>
          mergeTagOptions(prev, response?.results || [], reset || page === 1)
        );

        setTagHasMore(!!response.next);
      } catch (error) {
        console.error("Error loading tags:", error);
      } finally {
        setTagSearching(false);
      }
    },
    [dispatch, token, tagSearching, mergeTagOptions]
  );

  const handleTagInputChange = useCallback(
    (newInputValue) => {
      setTagInputValue(newInputValue);
      setTagPage(1);

      // Debounce search
      const timeoutId = setTimeout(() => {
        loadTags(1, newInputValue, true);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [loadTags]
  );

  const selectedTagIds = watch("tags");
  const selectedTags = useMemo(() => {
    if (!Array.isArray(selectedTagIds)) return [];
    const map = new Map();
    tagOptions?.forEach((tag) => {
      if (tag?.id) {
        map.set(tag.id, tag);
      }
    });
    return selectedTagIds.map((id) => map.get(id)).filter(Boolean);
  }, [selectedTagIds, tagOptions]);

  const handleTagSelectionChange = useCallback(
    (event, newValue, field) => {
      const validTags = (newValue || []).filter((tag) => tag?.id);
      field.onChange(validTags.map((tag) => tag.id));
      setTagOptions((prev) => mergeTagOptions(prev, validTags));
    },
    [mergeTagOptions]
  );

  const handleScrollTagList = useCallback(
    (event) => {
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
        if (tagHasMore && !tagSearching) {
          const nextPage = tagPage + 1;
          setTagPage(nextPage);
          loadTags(nextPage, tagInputValue);
        }
      }
    },
    [tagHasMore, tagSearching, tagPage, tagInputValue, loadTags]
  );

  const loadEmployees = useCallback(
    async (page = 1, search = "", reset = false) => {
      if (employeeSearching) return;

      setEmployeeSearching(true);
      try {
        const response = await axios.get(`${backend_api}contact/retrieve/`, {
          params: {
            page: page,
            // page_size: 20,
            search: search,
            contact_type: "person", // assuming employees are individual contacts
          },
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (reset || page === 1) {
          setEmployeeOptions(response.data.results || []);
        } else {
          setEmployeeOptions((prev) => [
            ...prev,
            ...(response.data.results || []),
          ]);
        }

        setEmployeeHasMore(!!response.data.next);
      } catch (error) {
        console.error("Error loading employees:", error);
      } finally {
        setEmployeeSearching(false);
      }
    },
    [token, employeeSearching]
  );

  const handleEmployeeInputChange = useCallback(
    (newInputValue) => {
      setEmployeeInputValue(newInputValue);
      setEmployeePage(1);

      // Debounce search
      const timeoutId = setTimeout(() => {
        loadEmployees(1, newInputValue, true);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [loadEmployees]
  );

  const handleEmployeeSelectionChange = (event, newValue, field) => {
    setSelectedEmployees(newValue);
    field.onChange(newValue.map((employee) => employee.id));
  };

  const handleScrollEmployeeList = useCallback(
    (event) => {
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
        if (employeeHasMore && !employeeSearching) {
          const nextPage = employeePage + 1;
          setEmployeePage(nextPage);
          loadEmployees(nextPage, employeeInputValue);
        }
      }
    },
    [
      employeeHasMore,
      employeeSearching,
      employeePage,
      employeeInputValue,
      loadEmployees,
    ]
  );

  return (
    <Box sx={{ height: "93vh", display: "flex" }}>
      <Box
        sx={{
          width: "20%",
          p: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative",
        }}
      >
        <Stepper
          orientation="vertical"
          activeStep={activeIndex}
          nonLinear
          connector={
            <Box
              sx={{
                ml: 0.5,
                height: "1.4rem",
                borderLeft: "1px dotted #bdbdbd",
              }}
            />
          }
        >
          {steps.map((step, index) => (
            <Step key={index} completed={false}>
              <StepLabel StepIconComponent={DotStepIcon}>
                {step.label}
              </StepLabel>
              <StepContent>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: "0.6rem" }}
                >
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        sx={{
          width: "80%",
          position: "relative",
          overflowY: "auto",
          paddingBottom: "5rem",
          ...pageScrollStyle,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, handleError)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                p: "1rem",
              }}
            >
              <Box
                sx={{
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => setActiveIndex(0)}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  Company Details
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Controller
                    name="company_name"
                    control={control}
                    rules={{ required: "Company name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <span>
                            Company Name<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        size="small"
                        fullWidth
                        error={!!errors.company_name}
                        helperText={errors.company_name?.message}
                      />
                    )}
                  />
                  <Controller
                    name="tags"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <Autocomplete
                          multiple
                          options={tagOptions}
                          getOptionLabel={(option) =>
                            typeof option === "string" ? option : option.name
                          }
                          value={selectedTags}
                          inputValue={tagInputValue}
                          onInputChange={(_, newInputValue) => {
                            handleTagInputChange(newInputValue);
                          }}
                          onChange={(_, newValue) =>
                            handleTagSelectionChange(_, newValue, field)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          renderTags={(tagValues, getTagProps) =>
                            tagValues?.map((option, index) => (
                              <Chip
                                size="small"
                                key={option.id}
                                label={option.name}
                                sx={{ bgcolor: option?.metadata?.color }}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tag"
                              placeholder="Select tags"
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {tagSearching && (
                                      <CircularProgress
                                        size={20}
                                        sx={{ mr: 1 }}
                                      />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          noOptionsText={
                            <NoDataOptionField
                              imgSrc={noOption}
                              text={"Oops! No Tags to display."}
                              btnText={"Tags"}
                              handleClickNoData={() => {
                                showDialog(<TagForm />, {
                                  dialogTitle: "Create Tag",
                                });
                              }}
                            />
                          }
                          ListboxComponent={(props) => (
                            <ListboxComponent
                              {...props}
                              btnText="Add tag"
                              btnIcon={<AddRounded />}
                              handleClikListBtn={() => {
                                showDialog(<TagForm />, {
                                  dialogTitle: "Create Tag",
                                });
                              }}
                            />
                          )}
                          ListboxProps={{
                            onScroll: handleScrollTagList,
                            sx: pageScrollStyle,
                          }}
                        />
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => setActiveIndex(1)}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  Contact Details
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Controller
                      name="reminder_mail"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="mobile"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Mobile No"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      name="website_url"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Website URL"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Box sx={{ width: "100%" }}>
                      <Controller
                        name="address_1"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            multiline
                            rows={3}
                            fullWidth
                            label="Full Address"
                            placeholder="Enter the full address based on the verified ID."
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "1rem" }}>
                        <Controller
                          name="city"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="City"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                        <Controller
                          name="state"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="State"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ display: "flex", gap: "1rem" }}>
                        <Controller
                          name="pincode"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Pin code"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                        <Controller
                          name="country"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Country"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  opacity: 1,
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  height: "100%",
                }}
                onClick={() => {
                  setActiveIndex(2);
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Custom Fields & Addition info
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                      <Controller
                        name="custom_fields"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            multiple
                            fullWidth
                            limitTags={4}
                            options={allCustomField || []}
                            getOptionLabel={(option) =>
                              option?.field_name || ""
                            }
                            value={selectedCustomFields}
                            onChange={handleCustomFieldSelectionChange}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Custom Field"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderTags={(tagValues, getTagProps) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  overflowX: "auto",
                                  maxHeight: "2.5rem",
                                  maxWidth: "18rem",
                                }}
                              >
                                {tagValues?.map((option, index) => (
                                  <Chip
                                    size="small"
                                    key={option.id}
                                    label={option.field_name}
                                    {...getTagProps({ index })}
                                  />
                                ))}
                              </Box>
                            )}
                            noOptionsText={
                              <NoDataOptionField
                                imgSrc={noOption}
                                text={
                                  "Oops! Looks like there are no Custom Field to display"
                                }
                                btnText={"Custom Field"}
                                handleClickNoData={() => {
                                  showDialog(<CustomFieldForm />, {
                                    dialogTitle: "Create Custom Field",
                                  });
                                }}
                              />
                            }
                            ListboxComponent={(props) => (
                              <ListboxComponent
                                {...props}
                                btnText="Add Custom Field"
                                btnIcon={<AddRounded />}
                                handleClikListBtn={() => {
                                  showDialog(
                                    <CustomFieldForm
                                      forWhichForm={"contact"}
                                    />,
                                    {
                                      dialogTitle: "Create Custom Field",
                                    }
                                  );
                                }}
                              />
                            )}
                          />
                        )}
                      />
                      <Controller
                        name="ledes_code"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="LEDES Code"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "1rem",
                        width: "100%",
                        maxHeight: "8rem",
                        py: "0.5rem",
                        overflow: "auto",
                        ...pageScrollStyle,
                      }}
                    >
                      {custom_fields && custom_fields?.length > 0 ? (
                        custom_fields?.map((fieldId, i) => {
                          const fieldData = allCustomField?.find(
                            (field) => field?.id === fieldId
                          );
                          if (!fieldData) return null;

                          return (
                            <Box
                              key={fieldData.id}
                              sx={{
                                flex: {
                                  xs: "0 0 100%",
                                  sm: "0 0 calc(50% - 0.5rem)",
                                  md: "0 0 calc(50% - 0.5rem)",
                                },
                                minWidth: 0,
                              }}
                            >
                              <Controller
                                name={`custom_fields_data.${i}`}
                                control={control}
                                rules={{
                                  required: fieldData.field_data?.required,
                                }}
                                defaultValue={{
                                  id: fieldData.id,
                                  name: fieldData.field_name,
                                  type: fieldData.field_data?.type,
                                  value: "",
                                }}
                                render={({ field }) => {
                                  if (fieldData.field_data?.type === "select") {
                                    return (
                                      <FormControl fullWidth>
                                        <Autocomplete
                                          value={field.value?.value || null}
                                          options={
                                            fieldData.field_data?.options || []
                                          }
                                          onChange={(_, newValue) => {
                                            const fieldValue =
                                              newValue?.value || newValue;
                                            field.onChange({
                                              id: fieldData.id,
                                              name: fieldData.field_name,
                                              type: fieldData.field_data?.type,
                                              value: fieldValue,
                                            });
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label={fieldData.field_name}
                                              size="small"
                                              required={
                                                fieldData.field_data?.required
                                              }
                                            />
                                          )}
                                        />
                                      </FormControl>
                                    );
                                  }

                                  if (fieldData.field_data?.type === "date") {
                                    return (
                                      <DatePicker
                                        value={
                                          field.value?.value
                                            ? dayjs(field.value.value)
                                            : null
                                        }
                                        onChange={(newValue) => {
                                          const dateValue = newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : null;
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: dateValue,
                                          });
                                        }}
                                        slotProps={{
                                          textField: {
                                            label: fieldData.field_name,
                                            size: "small",
                                            fullWidth: true,
                                            error: Boolean(
                                              errors?.custom_fields_data?.[i]
                                            ),
                                            helperText:
                                              errors?.custom_fields_data?.[i] &&
                                              `${fieldData.field_name} is required`,
                                          },
                                        }}
                                      />
                                    );
                                  }
                                  if (
                                    fieldData?.field_data?.type ===
                                    "Matter select"
                                  ) {
                                    return (
                                      <MatterApiField
                                        name={`custom_fields_data.${i}`}
                                        label={fieldData.field_name}
                                        value={field.value?.value || null}
                                        onChange={(value) => {
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: value,
                                          });
                                        }}
                                        errors={errors}
                                        token={token}
                                        apiUrl={`${backend_api}case/register/`}
                                        isRequired={
                                          fieldData?.field_data?.required
                                        }
                                        generateUniqueMatterName={(id, name) =>
                                          `000${id}-${name}`
                                        }
                                        AddMatterFunc={({ onCreated }) =>
                                          showDialog(
                                            <DrawerTemplate
                                              onMatterCreated={onCreated}
                                            />,
                                            {
                                              dialogTitle: "Select Template",
                                            }
                                          )
                                        }
                                      />
                                    );
                                  }
                                  if (
                                    fieldData?.field_data?.type ===
                                    "contact select"
                                  ) {
                                    return (
                                      <ContactApiField
                                        name={`custom_fields_data.${i}`}
                                        label={fieldData.field_name}
                                        value={field.value?.value || null}
                                        editData={
                                          field.value?.value
                                            ? { id: field.value.value }
                                            : null
                                        }
                                        onChange={(val) =>
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: val,
                                          })
                                        }
                                        errors={errors}
                                        token={token}
                                        apiUrl={`${backend_api}contact/retrieve/`}
                                        isRequired={
                                          fieldData?.field_data?.required
                                        }
                                      />
                                    );
                                  }
                                  // Handle text and other types
                                  return (
                                    <TextField
                                      label={fieldData.field_name}
                                      type={
                                        fieldData.field_data?.type || "text"
                                      }
                                      size="small"
                                      fullWidth
                                      value={field.value?.value || ""}
                                      required={fieldData?.field_data?.required}
                                      onChange={(e) =>
                                        field.onChange({
                                          id: fieldData.id,
                                          name: fieldData.field_name,
                                          type: fieldData.field_data?.type,
                                          value: e.target.value,
                                        })
                                      }
                                      error={Boolean(
                                        errors?.custom_fields_data?.[i]
                                      )}
                                      helperText={
                                        errors?.custom_fields_data?.[i] &&
                                        `${fieldData.field_name} is required`
                                      }
                                    />
                                  );
                                }}
                              />
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem", fontWeight: 400 }}
                        >
                          Select custom Field for additional data.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => setActiveIndex(3)}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  Employees Preferences
                </Typography>
                <Controller
                  name="other_contact"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <Autocomplete
                        multiple
                        options={employeeOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : `${option.first_name || ""} ${
                                option.last_name || ""
                              }`.trim() ||
                              option.company_name ||
                              "Unknown"
                        }
                        value={selectedEmployees}
                        inputValue={employeeInputValue}
                        onInputChange={(_, newInputValue) => {
                          handleEmployeeInputChange(newInputValue);
                        }}
                        onChange={(_, newValue) =>
                          handleEmployeeSelectionChange(_, newValue, field)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderTags={(employeeValues, getTagProps) =>
                          employeeValues?.map((option, index) => (
                            <Chip
                              size="small"
                              key={option.id}
                              label={
                                `${option.first_name || ""} ${
                                  option.last_name || ""
                                }`.trim() ||
                                option.company_name ||
                                "Unknown"
                              }
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Employees"
                            placeholder="Select employees"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {employeeSearching && (
                                    <CircularProgress
                                      size={20}
                                      sx={{ mr: 1 }}
                                    />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        noOptionsText={
                          <NoDataOptionField
                            imgSrc={noOption}
                            text={"Oops! No Employees to display."}
                            btnText={"Contact"}
                            handleClickNoData={() => {
                              showDialog(
                                <ContactMiniForm defaultType="person" />,
                                {
                                  dialogTitle: "Create Person Contact",
                                }
                              );
                            }}
                          />
                        }
                        ListboxComponent={(props) => (
                          <ListboxComponent
                            {...props}
                            btnText="Add Employee"
                            btnIcon={<AddRounded />}
                            handleClikListBtn={() => {
                              showDialog(
                                <ContactMiniForm defaultType="person" />,
                                {
                                  dialogTitle: "Create Person Contact",
                                }
                              );
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body1">
                              {option?.first_name} {option?.last_name}
                            </Typography>
                            <Box>
                              <AccountCircleRounded
                                sx={{
                                  color: "#2e77f5",
                                  fontSize: "0.9rem",
                                }}
                              />
                            </Box>
                          </li>
                        )}
                        ListboxProps={{
                          onScroll: handleScrollEmployeeList,
                          sx: pageScrollStyle,
                        }}
                      />
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderTop: "1px solid #FFFFFF2E",
                p: "0.5rem 1rem",
                gap: "1rem",
                zIndex: 10,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleClearForm}
                sx={{ fontSize: "0.8rem" }}
              >
                Clear form
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ fontSize: "0.8rem" }}
                onClick={handleCloseAddContect}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                size="small"
                type="submit"
                sx={{ fontSize: "0.8rem" }}
              >
                {isEditContactMode ? "Update company" : "Create company"}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default CompanyContactForm;
